import React, { useCallback, useMemo } from "react";
import { FlexGrowProps } from "styled-system";
import { CommercialNonWorkingDaysDatePicker } from "./CommercialNonWorkingDaysDatePicker";
import moment from "moment";
import { useGetCommercialNonWorkingDays } from "../../hooks/cachedQueries/useGetCommercialNonWorkingDays/useGetCommercialNonWorkingDays";

export interface CommercialNonWorkingDaysDatePickerProps {
  id: string;
  fieldId: string;
  value: string | undefined;
  label: string;
  displayDateFormat: string;
  placeholder: string;
  minDate?: Date;
  maxDate?: Date;
  openToDate?: Date;
}

export const CommercialNonWorkingDaysDatePickerContainer: React.FC<
  CommercialNonWorkingDaysDatePickerProps & FlexGrowProps
> = ({ value, openToDate, ...props }) => {
  const { commercialNonWorkingDays, apiError, loading } =
    useGetCommercialNonWorkingDays();

  const lookupSet = useMemo(
    () => new Set(commercialNonWorkingDays.map((value) => value.day)),
    [commercialNonWorkingDays],
  );

  const isDayEnabled = useCallback(
    (date: Date): boolean => {
      if (apiError || loading) {
        return false;
      }
      const excludedWorkDay = lookupSet.has(moment(date).format("YYYY-MM-DD"));
      const day = date.getDay();
      const weekday = day !== 0 && day !== 6;
      return weekday && !excludedWorkDay;
    },
    [lookupSet, apiError, loading],
  );

  const calculateOpenToDate = useCallback(() => {
    if (openToDate) {
      return openToDate;
    }

    for (let i = 0; i < 30; i++) {
      const selectedMoment = value ? moment(value, "YYYY-MM-DD") : moment();
      const candidate = selectedMoment.add(i, "days").toDate();
      if (!isDayEnabled(candidate)) {
        continue;
      }
      return candidate;
    }
  }, [isDayEnabled, value, openToDate]);

  return (
    <CommercialNonWorkingDaysDatePicker
      {...props}
      value={value}
      isDayEnabled={isDayEnabled}
      openToDate={calculateOpenToDate()}
    />
  );
};
